import merge from 'lodash/merge';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import type { Direction, Theme, ThemeOptions } from '@mui/material';
import { lightShadows } from './shadows';

interface ThemeConfig {
  direction?: Direction;
  responsiveFontSizes?: boolean;
  roundedCorners?: boolean;
  theme?: string;
}

interface Background2 {
  disabled: string
  black: string,
  white: string,
  neutral: string
}

interface Text2 {
  black: string,
  white: string,
}

declare module '@mui/material/styles' {
  interface Palette {
    gold: Palette['primary'];
    background2: Background2;
    text2: Text2;
  }
  interface PaletteOptions {
    gold: PaletteOptions['primary'];
    background2: Background2;
    text2: Text2;
  }
}

const baseOptions: ThemeOptions = {
  direction: 'rtl',
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
        },
        '#root': {
          height: '100%',
        },
        '#nprogress .bar': {
          zIndex: '2000 !important',
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
  typography: {
    button: {
      fontWeight: 600,
    },
    fontFamily:
      'IRANSans, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontWeight: 600,
      fontSize: '3.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '3rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.25rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    overline: {
      fontWeight: 600,
    },
  }
};

const themesOptions: ThemeOptions = {
  components: {
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 0.86,
            color: '#42526e',
          },
        },
      },
    },
  },
  palette: {
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    background2: {
      disabled: '#889A9C',
      black: '#171C15',
      white: '#ffffff',
      neutral: 'rgba(136, 154, 156, 0.1)'
    },
    text: {
      primary: '#004D33',
      secondary: '#253858',
    },
    text2: {
      black: '#171C15',
      white: '#ffffff',
    },
    primary: {
      main: '#004D33',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#111224',
      contrastText: '#ffffff'
    },
    success: {
      main: '#00714B',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FAA500',
      contrastText: '#ffffff',
    },
    error: {
      main: '#9C0101',
      contrastText: '#ffffff',
    },
    gold: {
      main: '#DAB17A',
      contrastText: '#ffffff'
    },
  },
  shadows: lightShadows,
};

export const createCustomTheme = (config: ThemeConfig = {}): Theme => {
  let theme = createTheme(
    merge(
      {},
      baseOptions,
      themesOptions,
      {
        ...(config.roundedCorners && {
          shape: {
            borderRadius: 16,
          },
        }),
      },
      {
        direction: config.direction,
      },
    ),
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
